import { useMemo } from "react";
import { Button, Modal } from "react-bootstrap";

const asText = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "number") return `${value}`;
    if (typeof value === "string") return value.trim() === "" ? "-" : value;
    if (typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch {
            return "-";
        }
    }
    return "-";
};

const parseMeta = (meta) => {
    if (!meta) return null;
    if (typeof meta === "object") return meta;
    if (typeof meta !== "string") return null;
    try {
        return JSON.parse(meta);
    } catch {
        return meta;
    }
};

const asYesNo = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "number") return value === 1 ? "Yes" : value === 0 ? "No" : `${value}`;
    if (typeof value === "string") {
        const v = value.trim().toLowerCase();
        if (v === "1" || v === "true" || v === "yes") return "Yes";
        if (v === "0" || v === "false" || v === "no") return "No";
        return value;
    }
    return asText(value);
};

const KeyValueGrid = ({ items }) => (
    <div className="row">
        {items.filter((x) => x).map((item) => (
            <div key={item.label} className="col-lg-6 py-3">
                <h6 className="mb-1">{item.label}</h6>
                <div className="text-muted">{item.value}</div>
            </div>
        ))}
    </div>
);

const getProfileId = (profile) => profile?.profileId || profile?.id || profile?.profile_id || profile?.profileID || profile?.profileID;
const getCustodianUserId = (profile) => profile?.custodianUserId || profile?.custodianUserID || profile?.custodian_user_id;
const getCustodianName = (profile) => profile?.custodianName || profile?.custodianFullName || profile?.custodian || "-";

const UserLink = ({ userId, children }) => {
    if (!userId) return children ?? "-";
    return (
        <a href={`/users/view/${userId}`} className="text-primary text-hover-primary">
            {children ?? userId}
        </a>
    );
};

export default function TroveNextTransitionModal({
    show,
    onHide,
    profile,
    onConfirm,
    title = "Transition User",
    message = "Are you sure you want to transition this user?",
    confirmLabel = "Transition User",
    isLoading,
}) {
    const profileId = useMemo(() => getProfileId(profile), [profile]);

    return (
        <Modal show={show} onHide={onHide} centered backdrop={"static"}>
            <Modal.Header closeButton={false}>
                <Modal.Title>{title}</Modal.Title>
                <button type="button" className="close" onClick={onHide} disabled={isLoading}>
                    <span aria-hidden="true" className="d-block">×</span>
                    <span className="sr-only">Close</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-4">{message}</div>
                {profile && (
                    <div className="mb-4" style={{ maxHeight: 360, overflowY: "auto" }}>
                        <div className="p-3 mb-3 rounded-sm bg-light-primary text-primary">
                            <div className="d-flex flex-wrap">
                                <div className="mr-6">
                                    <span className="font-weight-bolder">Consent Given:</span>{" "}
                                    {asYesNo(profile?.consentGiven)}
                                </div>
                                <div>
                                    <span className="font-weight-bolder">Questionnaire Completed:</span>{" "}
                                    {asYesNo(profile?.questionnaireCompleted)}
                                </div>
                            </div>
                        </div>
                        <KeyValueGrid
                            items={[
                                { label: "User Group", value: asText(profile?.userGroup) },
                                {
                                    label: "Custodian",
                                    value: (
                                        <UserLink userId={getCustodianUserId(profile)}>
                                            {asText(getCustodianName(profile))}
                                        </UserLink>
                                    ),
                                },
                                { label: "Full Name", value: asText(profile?.fullName) },
                                { label: "First Name", value: asText(profile?.firstName) },
                                { label: "Last Name", value: asText(profile?.lastName) },
                                { label: "Middle Name", value: asText(profile?.middleName) },
                                { label: "DOB", value: asText(profile?.dob) },
                                { label: "Gender", value: asText(profile?.gender) },
                                { label: "NIN", value: asText(profile?.nin) },
                                { label: "Email", value: asText(profile?.email) },
                                { label: "Phone", value: asText(profile?.phone) },
                                { label: "Market", value: asText(profile?.market) },
                                { label: "Status", value: asText(profile?.status) },
                                { label: "Relationship", value: asText(profile?.relationship) },
                                { label: "Consent Given", value: asYesNo(profile?.consentGiven) },
                                { label: "Questionnaire Completed", value: asYesNo(profile?.questionnaireCompleted) },
                                { label: "Meta", value: asText(parseMeta(profile?.meta)) },
                                { label: "Created", value: asText(profile?.created) },
                                { label: "Updated", value: asText(profile?.updated) },
                                { label: "Reconciliation Active", value: asYesNo(profile?.reconciliationActive) },
                            ]}
                        />
                    </div>
                )}
                <Button
                    type="button"
                    variant={"dark"}
                    className="w-100"
                    onClick={onConfirm}
                    disabled={!profileId || isLoading}
                >
                    {confirmLabel}
                </Button>
            </Modal.Body>
        </Modal>
    );
}
